
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { blogPosts } from '@/components/blog/BlogData';
import BlogSubscribe from '@/components/blog/BlogSubscribe';
import PopularPost from '@/components/blog/PopularPost';
import MobileFriendlyBlogPost from '@/components/blog/MobileFriendlyBlogPost';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { event, trackSEOEvent } from '@/utils/analytics';
import { 
  CybersecurityBestPractices, 
  CloudMigrationGuide, 
  DefaultBlogPost,
  ITDowntimeCost,
  ITSupportModel,
  HIPAACompliance,
  SecureDocumentManagement,
  RealEstateTech,
  ZeroTrustSecurity,
  NetworkSegmentation,
  DisasterRecoveryPlanning,
  CybersecurityLandlords,
  VantaPartnership
} from '@/components/blog/posts';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const isMobile = useIsMobile();
  const referrer = document.referrer;

  useEffect(() => {
    // Find the post with the matching slug
    const currentPost = blogPosts.find(post => post.slug === slug);
    
    if (currentPost) {
      setPost(currentPost);
      
      // Log page view for analytics
      event({
        action: 'blog_post_view',
        category: 'content',
        label: currentPost.title
      });
      
      // Track SEO event for this blog post
      const source = getReferrerType(referrer);
      trackSEOEvent({
        action: 'impression',
        source: source,
        page: `/blog/${slug}`
      });
      
      // Find related posts (same category, excluding current post)
      const related = blogPosts
        .filter(p => p.category === currentPost.category && p.id !== currentPost.id)
        .slice(0, 3);
      
      setRelatedPosts(related);
    }
    
    // Scroll to top on post change
    window.scrollTo(0, 0);
  }, [slug, referrer]);

  // Helper function to determine referrer type
  const getReferrerType = (ref: string): 'organic' | 'social' | 'referral' | 'direct' => {
    if (!ref) return 'direct';
    if (ref.includes('google') || ref.includes('bing') || ref.includes('yahoo')) return 'organic';
    if (ref.includes('facebook') || ref.includes('twitter') || ref.includes('linkedin') || 
        ref.includes('instagram') || ref.includes('pinterest')) return 'social';
    return 'referral';
  };

  // Get the appropriate blog post content based on slug
  const getPostContent = () => {
    if (slug === 'vanta-partnership-cybersecurity-compliance-ny-nj') {
      return <VantaPartnership />;
    }
    if (slug === 'cybersecurity-best-practices-small-business') {
      return <CybersecurityBestPractices />;
    }
    if (slug === 'cloud-migration-guide-small-businesses') {
      return <CloudMigrationGuide />;
    }
    if (slug === 'cost-of-it-downtime-prevention') {
      return <ITDowntimeCost />;
    }
    if (slug === 'choosing-it-support-model-business-growth') {
      return <ITSupportModel />;
    }
    if (slug === 'hipaa-compliance-it-requirements-healthcare') {
      return <HIPAACompliance />;
    }
    if (slug === 'secure-document-management-cpa-firms') {
      return <SecureDocumentManagement />;
    }
    if (slug === 'technology-solutions-real-estate-agencies') {
      return <RealEstateTech />;
    }
    if (slug === 'zero-trust-security-small-businesses') {
      return <ZeroTrustSecurity />;
    }
    if (slug === 'network-segmentation-small-business') {
      return <NetworkSegmentation />;
    }
    if (slug === 'disaster-recovery-business-continuity-planning-smb-framework') {
      return <DisasterRecoveryPlanning />;
    }
    if (slug === 'cybersecurity-landlords-property-managers-level-up') {
      return <CybersecurityLandlords />;
    }
    // Default content for other posts
    return <DefaultBlogPost />;
  };

  if (!post) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container-wide py-16">
          <div className="text-center">
            <h1 className="heading-lg mb-4">Post Not Found</h1>
            <Button asChild>
              <Link to="/blog">Return to Blog</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      <SEO 
        title={`${post.title} | IT Management Solutions Blog`}
        description={post.excerpt}
        canonicalUrl={`https://itmgmtsol.com/blog/${post.slug}`}
      />
      <Navbar />
      
      <section className={`py-8 md:py-12 ${isMobile ? 'px-4' : 'px-6'}`}>
        <div className="container-wide">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-itmgmt-medium-blue hover:text-itmgmt-bright-blue mb-6"
            onClick={() => {
              trackSEOEvent({
                action: 'click',
                source: 'direct',
                page: '/blog'
              });
            }}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to all posts
          </Link>
          
          <MobileFriendlyBlogPost
            title={post.title}
            date={post.date}
            author={post.author}
            authorTitle={post.authorTitle}
            content={getPostContent()}
            image={post.image}
            readTime={post.readTime}
            category={post.category}
          />
          
          {relatedPosts.length > 0 && (
            <div className="mt-16">
              <h3 className="heading-md mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(relatedPost => (
                  <PopularPost 
                    key={relatedPost.id} 
                    post={relatedPost} 
                    onClick={() => {
                      trackSEOEvent({
                        action: 'click',
                        source: 'direct',
                        page: `/blog/${relatedPost.slug}`
                      });
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      
      <BlogSubscribe />
      
      <Footer />
    </div>
  );
};

export default BlogPost;
